import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./home.css";
import TribeletLogo from "../assets/Logos/Tribelet Logo Transparent.png";
import { useUser } from "../context/UserContext";
import { teams as teamsService, kits as kitsService } from "../services/supabase";
import { api } from "../services/api";
import { Team } from "../types/database";
import SiteNav from "../components/SiteNav";

const marqueeItems = [
  "Five-a-Side", "Sunday League", "Rugby Club", "Netball Team",
  "Hockey Club", "Cricket XI", "Running Club",
];

const kitScatterLayout = [
  { top: "6%", left: "6%", rotate: -14, scale: 0.85 },
  { top: "8%", left: "92%", rotate: 10, scale: 0.9 },
  { top: "90%", left: "4%", rotate: 16, scale: 0.8 },
  { top: "92%", left: "90%", rotate: -9, scale: 0.85 },
  { top: "3%", left: "40%", rotate: 6, scale: 0.7 },
  { top: "95%", left: "55%", rotate: -6, scale: 0.75 },
  { top: "30%", left: "10%", rotate: -20, scale: 0.7 },
  { top: "34%", left: "88%", rotate: 18, scale: 0.75 },
  { top: "55%", left: "3%", rotate: 8, scale: 0.65 },
  { top: "60%", left: "95%", rotate: -12, scale: 0.7 },
  { top: "15%", left: "65%", rotate: -8, scale: 0.6 },
  { top: "75%", left: "30%", rotate: 12, scale: 0.65 },
  { top: "20%", left: "25%", rotate: 5, scale: 0.6 },
  { top: "80%", left: "70%", rotate: -15, scale: 0.65 },
];

type TeamWithRole = Team & { userRole: "captain" | "member" };

const CountUp: React.FC<{ value: number; duration?: number }> = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value === 0) return;
    let start: number | null = null;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value, duration]);

  return <>{display}</>;
};


const Home: React.FC = () => {
  const { user, username } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [userTeams, setUserTeams] = useState<TeamWithRole[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [stats, setStats] = useState({ tribes: 0, kits: 0, players: 0, orders: 0 });
  const [teamLogos, setTeamLogos] = useState<string[]>([]);
  const [kitPreviews, setKitPreviews] = useState<string[]>([]);

  useEffect(() => {
    api.getStats().then(setStats).catch(() => {});
    teamsService.getAllLogos()
      .then(res => {
        const urls = (res.data || []).map((t: any) => (t.logo_thumb_url || t.logo_url) as string);
        setTeamLogos(urls);
      })
      .catch(() => {});
    kitsService.getAllPreviews()
      .then(res => {
        const urls = (res.data || [])
          .map((k: any) => k.preview_front_url as string)
          .filter(Boolean);
        setKitPreviews(urls);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const scrollTo = (location.state as any)?.scrollTo;
    if (scrollTo) {
      setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.state]);

  useEffect(() => {
    if (!user) return;

    const fetchTeams = async () => {
      setTeamsLoading(true);
      try {
        const [ownedRes, memberRes] = await Promise.all([
          teamsService.getByUser(user.id),
          teamsService.getTeamsWhereUserIsMember(user.id),
        ]);

        const ownedTeams: TeamWithRole[] = (ownedRes.data || []).map((t: Team) => ({
          ...t,
          userRole: "captain" as const,
        }));

        const memberOnlyTeams: TeamWithRole[] = (memberRes.data || [])
          .filter((t: Team) => t.created_by !== user.id)
          .map((t: Team) => ({ ...t, userRole: "member" as const }));

        const ownedIds = new Set(ownedTeams.map((t) => t.id));
        const merged = [
          ...ownedTeams,
          ...memberOnlyTeams.filter((t) => !ownedIds.has(t.id)),
        ];

        setUserTeams(merged);
      } finally {
        setTeamsLoading(false);
      }
    };

    fetchTeams();
  }, [user]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add("in"), i * 80);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    requestAnimationFrame(() => {
      document.querySelectorAll(".home-page .reveal").forEach((el) => io.observe(el));
    });
    return () => io.disconnect();
  }, []);

  return (
    <div className="home-page">
      <div className="grain-overlay" />

      {/* NAV */}
      <SiteNav />

      {/* HERO */}
      <section className="hero">
        <div className="hero-marker">
          Describe<br />
          Design<br />
          Deliver
        </div>
        <div>
          <img src={TribeletLogo} alt="Tribelet" className="hero-logo-mark" />
          <p className="hero-tagline">
            Turning <span className="strike">ordinary</span> groups <br />into <span className="em">extraordinary</span> teams.
          </p>
        </div>
        {teamLogos.length > 0 && (
          <div className="logo-marquee">
            <div className="logo-marquee-track">
              {Array.from({ length: Math.ceil(60 / teamLogos.length) * 2 }, () => teamLogos).flat().map((url, i) => (
                <img key={i} src={url} alt="" className="logo-marquee-img" />
              ))}
            </div>
          </div>
        )}
        <div className="hero-bottom">
          {user ? (
            <button
              className="hero-cta"
              onClick={() => document.getElementById("tribes")?.scrollIntoView({ behavior: "smooth" })}
            >
              My Teams
              <span className="arrow">→</span>
            </button>
          ) : (
            <Link to="/build" className="hero-cta">
              Build Your Kit
              <span className="arrow">→</span>
            </Link>
          )}
          <p className="hero-meta">
            A kit-ordering service for the tribe you've already got — described,
            designed, and delivered.
          </p>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee">
        <div className="marquee-track">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </div>
      </div>

      {/* YOUR TRIBES */}
      {user && (
        <section className="tribes-section" id="tribes">
          <div className="section-header reveal">
            <div className="section-num">[ YOUR TRIBES ]</div>
            <h2 className="section-title">
              {username ? `${username}'s Teams` : "Your Teams"}
            </h2>
            <a href="#tribes" className="section-meta tribes-manage">
              Manage All →
            </a>
          </div>

          {teamsLoading ? (
            <p className="tribes-loading">Loading your tribes…</p>
          ) : userTeams.length === 0 ? (
            <div className="tribes-empty">
              <div className="tribes-empty-num">00</div>
              <p className="tribes-empty-title">No Teams Yet</p>
              <p className="tribes-empty-desc">Every great tribe has to start somewhere.</p>
              <Link to="/create" className="hero-cta tribes-empty-cta">
                Create Your First Team <span className="arrow">→</span>
              </Link>
            </div>
          ) : (
            <div className="tribes-grid">
              {userTeams.map((team) => (
                <div
                  key={team.id}
                  className={`tribe-card ${team.userRole}`}
                  onClick={() => navigate(`/team/${team.id}`)}
                >
                  <div className="tribe-card-top">
                    {team.logo_url ? (
                      <img src={team.logo_url} alt={`${team.team_name} logo`} className="tribe-logo" />
                    ) : (
                      <div className="tribe-logo-placeholder">
                        {team.team_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className={`tribe-role ${team.userRole}`}>
                      {team.userRole === "captain" ? "Captain" : "Member"}
                    </span>
                  </div>
                  <p className="tribe-name">{team.team_name}</p>
                  {team.summary && <p className="tribe-summary">{team.summary}</p>}
                  <div className="tribe-card-footer">
                    <span className="tribe-date">
                      {new Date(team.created_at).toLocaleDateString("en-GB", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </span>
                    <span className="tribe-link">View →</span>
                  </div>
                </div>
              ))}
              <Link to="/create" className="tribe-card tribe-new">
                <div className="tribe-new-inner">
                  <div className="tribe-new-plus">+</div>
                  <p className="tribe-new-label">Start a New Team</p>
                </div>
              </Link>
            </div>
          )}
        </section>
      )}

      {/* FINAL CTA */}
      {!user && (
        <section className="final-cta" id="tribes">
          {kitPreviews.length > 0 && (
            <div className="final-cta-kits">
              {kitPreviews.slice(0, kitScatterLayout.length).map((url, i) => {
                const pos = kitScatterLayout[i];
                // Keep each image's center at least half its own (scaled) size from
                // every edge so it can never bleed past the container and get clipped.
                const halfW = `calc(clamp(55px, 6.5vw, 110px) * ${pos.scale})`;
                const halfH = `calc(clamp(55px, 6.5vw, 110px) * ${pos.scale} * 1.15)`;
                return (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="final-cta-kit-img"
                    style={{
                      top: `clamp(${halfH}, ${pos.top}, calc(100% - (${halfH})))`,
                      left: `clamp(${halfW}, ${pos.left}, calc(100% - (${halfW})))`,
                      transform: `translate(-50%, -50%) rotate(${pos.rotate}deg) scale(${pos.scale})`,
                    }}
                  />
                );
              })}
            </div>
          )}
          <div className="final-cta-content reveal">
            <h2>
              Your team is already a <span className="em">tribe</span>. <br />
              Now make them <em>look</em> like one.
            </h2>
            <Link to="/build" className="big-cta">Start Your Tribe →</Link>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="how" id="how">
        <div className="section-header reveal">
          <h2 className="section-title">How It Works</h2>
          <div className="section-meta">Three Steps · Five Minutes</div>
        </div>
        <div className="steps">
          <div className="step reveal">
            <div className="step-icon">// 001</div>
            <div className="step-num">01</div>
            <h3 className="step-title">Describe Your Tribe</h3>
            <p className="step-desc">Describe your team in a few lines: who you are, what sport you play, when and where you play. Feel free to add any defining info!</p>
          </div>
          <div className="step reveal">
            <div className="step-icon">// 002</div>
            <div className="step-num">02</div>
            <h3 className="step-title">Design Your Kit</h3>
            <p className="step-desc">Choose the t-shirt colour, refine your logo, and select the material for printing.</p>
          </div>
          <div className="step reveal">
            <div className="step-icon">// 003</div>
            <div className="step-num">03</div>
            <h3 className="step-title">Delivered Within Two Weeks</h3>
            <p className="step-desc">Order the kit, and receive delivery within a fortnight. If you need more time, save your work and come back later!</p>
          </div>
        </div>
      </section>

      {/* DICTIONARY */}
      <section className="dict-section" id="manifesto">
        <div className="dict-content reveal">
          <div className="dict-entry">
            <div className="dict-word">tribelet</div>
            <div className="dict-locale">in American English</div>
            <div className="dict-phonetic">('traɪblɪt)</div>
            <div className="dict-pos">noun</div>
            <p className="dict-def">
              a tribe having few members, esp. one that is a component of a larger tribal organisation
            </p>
            <div className="dict-source">
              Most material © 2005, 1997, 1991 by Penguin Random House LLC. Modified entries © 2019 by Penguin Random House LLC and HarperCollins Publishers Ltd
            </div>
          </div>
          <div className="dict-story">
            <p>
              Tribelet started in 2025, with two grads - each running a small, casual sports team - enjoying the community of self-organised leagues in London, but struggling to source quality, custom kits to create a team identity.
            </p>
            <p>
              The ambition was to build a platform where any small team starting out could quickly generate an espirit de corps without the usual administrative hassle or quality sacrifice involved with custom kit production.
            </p>
            <p>
              The idea is to improve the wider sports <em>Tribe</em> by providing a space for small groups to create and organise their <em>Tribelets</em>.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="teams-section">
        <div className="teams-grid">
          <div className="team-stat reveal">
            <div className="team-num"><CountUp value={stats.tribes} /></div>
            <div className="team-label">Tribes</div>
          </div>
          <div className="team-stat reveal">
            <div className="team-num"><CountUp value={stats.kits} /></div>
            <div className="team-label">Kits</div>
          </div>
          <div className="team-stat reveal">
            <div className="team-num"><CountUp value={stats.players} /></div>
            <div className="team-label">Players</div>
          </div>
          <div className="team-stat reveal">
            <div className="team-num"><CountUp value={stats.orders} /></div>
            <div className="team-label">Orders</div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="home-footer" id="contact">
        <div className="footer-top">
          <div>
            <a
              href="#"
              className="footer-logo"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              TRIBELET.
            </a>
            <p className="footer-tag">Turning ordinary groups<br />into extraordinary teams.</p>
          </div>
          <div className="footer-cols">
            <div className="footer-col">
              <h4>Account</h4>
              <a href="#tribes" onClick={(e) => { e.preventDefault(); document.getElementById('tribes')?.scrollIntoView({ behavior: 'smooth' }); }}>My Teams</a>
              <Link to="/orders">My Orders</Link>
              <Link to="/profile">Profile</Link>
            </div>
            <div className="footer-col">
              <h4>Help</h4>
              <a href="/#contact">Contact</a>
              <a href="mailto:hello@tribelet.co.uk">Email Us</a>
              <a href="https://www.instagram.com/tribeletco" target="_blank" rel="noreferrer">Instagram</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} Tribelet · All Rights Reserved</span>
          <span>London · Made to Order</span>
        </div>
      </footer>
    </div>
  );
};

export default Home;
